/**
 * Live-first DeckPath → DeckId resolution for WRITE paths.
 *
 * A deck path is not unique: server-side backup recovery historically minted a
 * new deck that kept the original's `deckPaths`, so an account can hold a
 * tombstoned original and a live `[BACKUP]` recovery both carrying one path.
 * The READ resolver (`deck/deck-locator`) resolves a locator
 * live-decks-first, so the screen shows the deck the user can actually see —
 * but the write-side resolvers (`sync.decks.findByDeckPath`, mobile's
 * `resolveDeckIdSnapshot`) used a raw insertion-order scan that still included
 * tombstones. On such an account every rename, card edit, delete and study
 * rating re-resolved the path to the DEAD twin and evaporated on the
 * `if (!existing) return` guards — reads and writes answering the same
 * question differently is the whole bug.
 *
 * This module is the one write-side answer: first LIVE deck carrying the
 * path, falling back to a tombstoned carrier only when no live deck has it
 * (deleted-deck flows — undo, archived views — still resolve).
 */

export type DeckPathRow = {
  deckPaths?: readonly string[] | null;
  deletedTimeISO?: string | null;
  addedTimeISO?: string | null;
};

/** Mirrors `isDeckVisible` (and `deck-locator`'s ordering predicate):
 *  a deck is live unless tombstoned, and a re-add AFTER the tombstone
 *  resurrects it. */
export const isLiveDeckRow = (p: DeckPathRow): boolean =>
  !p.deletedTimeISO || (!!p.addedTimeISO && p.deletedTimeISO < p.addedTimeISO);

/**
 * First LIVE deck id carrying `path` (map-insertion order among live decks),
 * else the first tombstoned carrier, else `undefined`.
 */
export function firstLiveDeckIdForPath(
  propsMap: Record<string, DeckPathRow | undefined>,
  path: string,
): string | undefined {
  let tombstoned: string | undefined;
  for (const [id, p] of Object.entries(propsMap)) {
    if (!p?.deckPaths?.includes(path)) continue;
    if (isLiveDeckRow(p)) return id;
    tombstoned ??= id;
  }
  return tombstoned;
}

/**
 * The name a deck SHOWS, for a key that is its id or one of its paths. A deck
 * path is a storage key — a manual deck's is an ASCII slug plus a random
 * suffix (`deck-manual-1a2b3c4d` for a Hebrew name) — so anything that hands
 * the deck's name to a model (Autofill, type switch, field rewrite) sends this
 * instead: on an empty deck it is the only topic and language hint. Falls back
 * to the key when the deck is unknown or unnamed.
 */
export function deckDisplayNameForKey(
  propsMap: Record<string, (DeckPathRow & { displayName?: string | null }) | undefined>,
  key: string,
): string {
  const id = propsMap[key] ? key : firstLiveDeckIdForPath(propsMap, key);
  return (id ? propsMap[id]?.displayName?.trim() : undefined) || key;
}
